import { Fragment, useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Input } from '@diwata/ironvale'
import { DiwaAPI } from '@/api/client'
import { GraphAPI } from '@/api/graph'
import { HelpHint } from '@/components/HelpHint'
import { IdentityEditor } from '@/components/IdentityEditor'
import { identityFields } from '@/lib/onboarding'
import { useMissionSelection } from '@/store/missionSelection'
import { usePrefill } from '@/store/prefill'
import { useGraphFocus } from '@/store/graphFocus'
import { useSelection, type Selected } from '@/store/selection'
import { useUI } from '@/store/ui'
import { DOMAIN_PALETTE, EGO, readNodeColor } from '@/lib/graphColors'

// The swatches offered in the Dossier: the domain palette + the ego's crimson. Picking one writes
// entity.attributes.color, which the constellation resolves ahead of the domain default.
const SWATCHES: string[] = [...DOMAIN_PALETTE, EGO.core]

// The Dossier: a star is a doorway. Its card, everything the app knows about it (notes that
// cite it, signals about it, missions targeting it, enrichment state), and the acts you'd
// take — right where you're looking. Replaces the old inspect card + the mockup actions tab.

function attr(attrs: Record<string, unknown> | null | undefined, key: string): string | null {
  const v = attrs?.[key]
  return v == null || v === '' ? null : String(v)
}

// The Name row's inline edit: pencil → input → save. Enter saves, Escape cancels; a failed
// save keeps the draft so nothing typed is lost.
function NameRow({ selected, onRename }: { selected: Selected; onRename: (name: string) => Promise<void> }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(false)

  if (!editing) {
    return (
      <>
        {selected.name}
        <button
          type="button"
          className="dw-dossier-edit"
          aria-label={`Rename ${selected.name}`}
          title="Rename"
          onClick={() => { setDraft(selected.name); setError(false); setEditing(true) }}
        >
          ✎
        </button>
      </>
    )
  }

  async function save() {
    const name = draft.trim()
    if (!name || name === selected.name) { setEditing(false); return }
    setSaving(true)
    setError(false)
    try {
      await onRename(name)
      setEditing(false)
    } catch {
      setError(true)
    } finally {
      setSaving(false)
    }
  }

  return (
    <form
      className="dw-dossier-rename"
      onSubmit={(e) => { e.preventDefault(); void save() }}
    >
      <Input
        autoFocus
        value={draft}
        error={error}
        aria-label="Star name"
        className="dw-dossier-rename-input"
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false) }}
      />
      <Button size="sm" type="submit" disabled={saving || !draft.trim()}>
        {saving ? '…' : 'Save'}
      </Button>
      <Button size="sm" variant="ghost" type="button" onClick={() => setEditing(false)}>
        Cancel
      </Button>
      {error && <span className="dw-dossier-rename-error">Could not rename.</span>}
    </form>
  )
}

function Card({ selected, onRename }: { selected: Selected; onRename: (name: string) => Promise<void> }) {
  const a = selected.attributes
  const rows: [ReactNode, ReactNode][] = [
    ['Name', <NameRow selected={selected} onRename={onRename} />],
    // Entity-type words aren't frozen platform terms — a literal gloss, per HelpHint's contract.
    [<HelpHint gloss="What kind of star this is — its entity type in the domain's vocabulary.">Type</HelpHint>, selected.type],
  ]
  const title = attr(a, 'title') ?? attr(a, 'role')
  if (title) rows.push(['Title', title])
  const company = attr(a, 'company')
  if (company) rows.push(['Company', company])
  if (selected.domain) rows.push([<HelpHint term="domain">Domain</HelpHint>, selected.domain])
  if (selected.score != null) rows.push(['Score', selected.score])
  const src = attr(a, 'src')
  if (src) rows.push(['Source', src])
  const note = attr(a, 'note')
  return (
    <>
      <div className="dw-field-grid">
        {rows.map(([label, value], i) => (
          <Fragment key={i}>
            <div className="dw-field-label">{label}</div>
            <div className="dw-field-value">{value}</div>
          </Fragment>
        ))}
      </div>
      {note && (
        <div style={{ marginTop: 8, fontSize: 'var(--ae-font-size-xs)', color: 'var(--ae-color-text-secondary)', lineHeight: 1.5 }}>
          {note}
        </div>
      )}
    </>
  )
}

// The Ego card — "You" is the one node that is not a contact/company/topic but the keeper
// themselves. It gets a distinct crimson-cored treatment (matching the star's core in the sky)
// and drops the misleading "Domain: crm" row: crm is only the ego's built-in home, and reading
// it as an attribute made "you" look like any mis-filed entity. Name stays inline-editable.
function EgoCard({ selected, onRename }: { selected: Selected; onRename: (name: string) => Promise<void> }) {
  const tagline = identityFields(selected.attributes?.identity).tagline
  return (
    <div className="dw-ego-head">
      <span className="dw-ego-mark" aria-hidden="true" />
      <div className="dw-ego-headings">
        <div className="dw-ego-kicker">You</div>
        <div className="dw-ego-name">
          <NameRow selected={selected} onRename={onRename} />
        </div>
        {tagline && <div className="dw-ego-tagline">{tagline}</div>}
        <div className="dw-ego-home">The center of your constellation</div>
      </div>
    </div>
  )
}

export function Dossier() {
  const { selected, select } = useSelection()
  const { diwaUrl } = useUI()
  const graphApi = useMemo(() => new GraphAPI(diwaUrl), [diwaUrl])
  const api = useMemo(() => new DiwaAPI(diwaUrl), [diwaUrl])
  const qc = useQueryClient()
  const navigate = useNavigate()
  const selectMission = useMissionSelection((s) => s.select)
  const prefill = usePrefill()
  const setFocus = useGraphFocus((st) => st.setFocus)
  const [enriching, setEnriching] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [confirmingRemove, setConfirmingRemove] = useState(false)
  const [removing, setRemoving] = useState(false)

  const { data: dossier } = useQuery({
    queryKey: ['dossier', selected?.id],
    queryFn: () => graphApi.dossier(selected!.id),
    enabled: Boolean(selected),
  })

  if (!selected) {
    return (
      <div>
        <div className="dw-rail-section-title">Dossier</div>
        <div className="dw-rail-empty">Select a star — its whole story assembles here.</div>
      </div>
    )
  }

  // The ego carries attributes.self (set by the network endpoint from the entity's stored
  // attributes). It is the only "you" node — differentiate its whole dossier.
  const isEgo = Boolean(selected.attributes?.self)
  const isPerson = selected.type === 'person'
  const enrichment = dossier?.enrichment ?? null

  // Reflect a saved identity onto the live selection so the ego card's tagline + the "About you"
  // view update at once, before the invalidated graph/dossier queries refetch.
  function onIdentitySaved(identity: Record<string, unknown>) {
    if (!selected) return
    select({ ...selected, attributes: { ...(selected.attributes ?? {}), identity } })
  }

  async function enrichNow() {
    setEnriching(true)
    setNotice(null)
    try {
      await api.post(`/people/${selected!.id}/enrich`, {})
      setNotice('Enrichment queued — the ◈ inbox will ask if the match is uncertain.')
      setTimeout(() => qc.invalidateQueries({ queryKey: ['dossier', selected!.id] }), 4000)
    } catch {
      setNotice('Could not queue enrichment. Try again.')
    } finally {
      setEnriching(false)
    }
  }

  function newMission() {
    prefill.setMissionGoal(`Regarding ${selected!.name}: `)
    navigate('/missions')
  }

  const trackSource = String(selected?.attributes?.['source'] ?? '')
  const untrackable = trackSource.startsWith('ledger:') || trackSource.startsWith('radar:')

  async function untrack() {
    if (!selected) return
    try {
      await api.post('/ledger/untrack', { entity_id: selected.id })
      setNotice('Untracked — back to the lake it goes. The row remains in the Ledger.')
      select(null)
    } catch {
      setNotice('Could not untrack this star.')
    }
  }

  function addNote() {
    prefill.setNoteCapture(`[[${selected!.name}]] `)
    navigate('/trove')
  }

  // Rename writes through the entity update path; the selection reflects it at once,
  // the query invalidations keep the constellation and dossier honest.
  async function rename(name: string) {
    if (!selected) return
    await graphApi.renameEntity(selected.id, name)
    select({ ...selected, name })
    qc.invalidateQueries({ queryKey: ['graph-network'] })
    qc.invalidateQueries({ queryKey: ['dossier', selected.id] })
  }

  // Removal detaches every edge touching the star server-side; history keeps the event.
  async function removeStar() {
    if (!selected) return
    setRemoving(true)
    setNotice(null)
    try {
      await graphApi.deleteEntity(selected.id)
      select(null) // the star is gone — nothing to keep selected
      qc.invalidateQueries({ queryKey: ['graph-network'] })
      qc.invalidateQueries({ queryKey: ['dossier'] })
    } catch {
      setNotice('Could not remove this star.')
    } finally {
      setRemoving(false)
      setConfirmingRemove(false)
    }
  }

  const currentColor = readNodeColor(selected.attributes)

  async function setColor(color: string | null) {
    if (!selected) return
    setNotice(null)
    try {
      await graphApi.setEntityColor(selected.id, color)
      // Reflect the override locally so the star recolours at once; refetch keeps the graph honest.
      const nextAttrs = { ...(selected.attributes ?? {}) }
      if (color) nextAttrs.color = color
      else delete nextAttrs.color
      select({ ...selected, attributes: nextAttrs })
      qc.invalidateQueries({ queryKey: ['graph-network', 'all'] })
      qc.invalidateQueries({ queryKey: ['dossier', selected.id] })
    } catch {
      setNotice('Could not set the colour.')
    }
  }

  return (
    <div>
      <div className="dw-rail-section-title">{isEgo ? 'You' : 'Dossier'}</div>
      {isEgo ? (
        <>
          <EgoCard selected={selected} onRename={rename} />
          <IdentityEditor
            egoId={selected.id}
            identity={selected.attributes?.identity}
            graphApi={graphApi}
            onSaved={onIdentitySaved}
          />
        </>
      ) : (
        <Card selected={selected} onRename={rename} />
      )}

      <div className="dw-rail-section-title" style={{ marginTop: 12 }}>Colour</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {SWATCHES.map((c) => {
          const on = currentColor?.toLowerCase() === c.toLowerCase()
          return (
            <button
              key={c}
              type="button"
              aria-label={`Set colour ${c}`}
              aria-pressed={on}
              onClick={() => setColor(on ? null : c)}
              style={{
                width: 18,
                height: 18,
                borderRadius: 999,
                background: c,
                cursor: 'pointer',
                padding: 0,
                border: on ? '2px solid var(--ae-color-text-primary)' : '1px solid var(--ae-color-border)',
                boxShadow: on ? `0 0 6px ${c}` : undefined,
              }}
            />
          )
        })}
        <button
          type="button"
          className="dw-chip dw-chip--toggle"
          aria-pressed={currentColor == null}
          onClick={() => setColor(null)}
          style={{ marginLeft: 4 }}
        >
          default
        </button>
      </div>

      {enrichment && (
        <div className="dw-dossier-enrichment">
          enrichment / {enrichment.status}
          {typeof enrichment.confidence === 'number' && ` · ${Math.round(enrichment.confidence * 100)}%`}
          {enrichment.enriched_at && ` · ${new Date(enrichment.enriched_at).toLocaleDateString()}`}
        </div>
      )}

      <div className="dw-dossier-actions">
        {isPerson && !isEgo && (
          <Button size="sm" variant="primary" disabled={enriching} onClick={enrichNow}>
            {enriching ? 'Queuing…' : 'Enrich now'}
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={() => { setFocus(selected!.id); navigate('/graph') }}>
          ◎ Center here
        </Button>
        <Button size="sm" variant="ghost" onClick={newMission}>✦ New mission</Button>
        <Button size="sm" variant="ghost" onClick={addNote}>Add note</Button>
        {untrackable && (
          <Button size="sm" variant="ghost" onClick={untrack}>Untrack</Button>
        )}
      </div>
      {notice && <div className="dw-notice" style={{ marginTop: 6 }}>{notice}</div>}

      {(dossier?.interactions?.length ?? 0) > 0 && (
        <>
          <div className="dw-rail-section-title" style={{ marginTop: 14 }}>Interactions</div>
          <div className="dw-mission-queue">
            {dossier!.interactions.map((i) => (
              <div key={i.id} className="dw-queue-row" style={{ cursor: 'default' }}>
                <span className="dw-queue-glyph">✉</span>
                <span className="dw-queue-title">
                  {i.summary || i.type}
                  <span className="dw-inbox-detail">
                    {i.channel ?? i.type}
                    {i.occurred_at && ` · ${new Date(i.occurred_at).toLocaleDateString()}`}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {(dossier?.notes.length ?? 0) > 0 && (
        <>
          <div className="dw-rail-section-title" style={{ marginTop: 14 }}>Notes citing it</div>
          <div className="dw-mission-queue">
            {dossier!.notes.map((n) => (
              <button key={n.id} type="button" className="dw-queue-row" onClick={() => navigate('/trove')}>
                <span className="dw-queue-glyph">✧</span>
                <span className="dw-queue-title">{n.title}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {(dossier?.signals.length ?? 0) > 0 && (
        <>
          <div className="dw-rail-section-title" style={{ marginTop: 14 }}>Signals about it</div>
          <div className="dw-mission-queue">
            {dossier!.signals.map((s) => (
              <button key={s.id} type="button" className="dw-queue-row" onClick={() => navigate('/signals')}>
                <span className={`dw-queue-glyph${s.status === 'fresh' ? ' dw-queue-glyph--gate' : ''}`}>●</span>
                <span className="dw-queue-title">
                  {s.title}
                  <span className="dw-inbox-detail">{s.status}</span>
                </span>
              </button>
            ))}
          </div>
        </>
      )}

      {(dossier?.missions.length ?? 0) > 0 && (
        <>
          <div className="dw-rail-section-title" style={{ marginTop: 14 }}>Missions targeting it</div>
          <div className="dw-mission-queue">
            {dossier!.missions.map((m) => (
              <button
                key={m.id}
                type="button"
                className="dw-queue-row"
                onClick={() => {
                  selectMission(m.id)
                  navigate(`/missions/${m.id}`)
                }}
              >
                <span className="dw-queue-glyph">◇</span>
                <span className="dw-queue-title">
                  {m.title}
                  <span className="dw-inbox-detail">{m.status}</span>
                </span>
              </button>
            ))}
          </div>
        </>
      )}

      {/* Danger zone: removal is destructive and rare — held behind one explicit confirm step.
          The ego can never be removed — "you" are the center the whole graph hangs from. */}
      {!isEgo && (
      <div className="dw-dossier-danger">
        {!confirmingRemove ? (
          <button type="button" className="dw-dossier-danger-trigger" onClick={() => setConfirmingRemove(true)}>
            Remove this star…
          </button>
        ) : (
          <div role="group" aria-label={`Confirm removing ${selected.name}`}>
            <div className="dw-dossier-danger-copy">
              This removes <strong>{selected.name}</strong> from the constellation and detaches every
              thread touching it. The history keeps a record.
            </div>
            <div className="dw-dossier-danger-row">
              <Button size="sm" variant="danger" disabled={removing} onClick={removeStar}>
                {removing ? 'Removing…' : 'Remove'}
              </Button>
              <Button size="sm" variant="ghost" autoFocus disabled={removing} onClick={() => setConfirmingRemove(false)}>
                Keep
              </Button>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  )
}
